import { defineStore } from 'pinia'
import type { MenuResponse } from '~/types/menu'

const flattenLeaves = (menus: MenuResponse[]): MenuResponse[] =>
  menus.flatMap(m => (m.children?.length ? flattenLeaves(m.children) : [m]))

export const useMenuStore = defineStore('menu', {
  state: () => ({
    menus: [] as MenuResponse[],
    loaded: false,
    selectedRootId: null as number | null,
    favoriteIds: [] as number[],
    favoritesMode: false,
  }),

  getters: {
    isFavorite: (state) => (menuId: number) =>
      state.favoriteIds.includes(menuId),

    favoriteMenus(state): MenuResponse[] {
      return flattenLeaves(state.menus).filter(m =>
        state.favoriteIds.includes(m.id)
      )
    },

    sidebarMenus(state): MenuResponse[] {
      if (state.favoritesMode) return (this as any).favoriteMenus
      if (state.selectedRootId === null) return state.menus
      const root = state.menus.find(m => m.id === state.selectedRootId)
      return root?.children ?? []
    },
  },

  actions: {
    async fetchMenus() {
      if (this.loaded) return
      // 토큰 확보(F5 직후 refresh)는 api.ts 요청 인터셉터에서 일괄 처리하므로
      // 여기서 별도로 다루지 않는다.
      const { $api } = useNuxtApp()
      try {
        // 메뉴와 즐겨찾기는 서로 독립적이므로 병렬로 가져와 직렬 왕복(1 RTT)을 줄인다.
        // fetchFavorites 는 내부에서 예외를 자체 처리하므로 Promise.all 을 reject 시키지 않는다.
        const [response] = await Promise.all([
          $api.get<{ data: MenuResponse[] }>('/api/v1/me/menus'),
          this.fetchFavorites(),
        ])
        this.menus = response.data.data
        this.loaded = true
        if (this.menus.length > 0 && this.selectedRootId === null) {
          this.selectedRootId = this.menus[0].id
        }
      } catch {
        this.menus = []
      }
    },

    async fetchFavorites() {
      const { fetchFavoriteIds } = useFavoriteMenus()
      try {
        const res = await fetchFavoriteIds()
        this.favoriteIds = res.data.data
      } catch {
        this.favoriteIds = []
      }
    },

    async toggleFavorite(menuId: number) {
      const { addFavorite, removeFavorite } = useFavoriteMenus()
      if (this.favoriteIds.includes(menuId)) {
        this.favoriteIds = this.favoriteIds.filter(id => id !== menuId)
        await removeFavorite(menuId).catch(() => {
          this.favoriteIds.push(menuId)
        })
      } else {
        this.favoriteIds.push(menuId)
        await addFavorite(menuId).catch(() => {
          this.favoriteIds = this.favoriteIds.filter(id => id !== menuId)
        })
      }
    },

    setSelectedRoot(id: number) {
      this.selectedRootId = id
      this.favoritesMode = false
    },

    /**
     * 현재 라우트 경로가 속한 1Depth 루트 메뉴로 selectedRootId 를 동기화한다.
     * 탭 클릭·딥링크·뒤로가기 등 상단메뉴를 직접 누르지 않고 이동한 경우에도
     * 좌측 사이드바(하위메뉴)와 상단메뉴 강조가 현재 화면과 일치하도록 맞춘다.
     * 즐겨찾기 뷰(favoritesMode)일 때는 사용자가 의도한 목록이므로 건드리지 않는다.
     */
    syncRootByPath(path: string) {
      if (this.favoritesMode || !path) return
      const contains = (m: MenuResponse): boolean =>
        m.path === path || (m.children ?? []).some(contains)
      const root = this.menus.find(contains)
      if (root && root.id !== this.selectedRootId) {
        this.selectedRootId = root.id
      }
    },

    setFavoritesMode() {
      this.favoritesMode = true
      this.selectedRootId = null
    },

    reset() {
      this.menus = []
      this.loaded = false
      this.selectedRootId = null
      this.favoriteIds = []
      this.favoritesMode = false
    },
  },
})
